import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  adminClearDeviceWindowCapturesMutation,
  adminDeviceWindowCapturesInfiniteOptions,
  adminWindowCaptureOptions,
} from '@/client/@tanstack/react-query.gen'
import { ClearCaptureAction } from '@/components/features/clear-capture-action'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { nextOffset } from '@/lib/offset-paging'
import { ADMIN_CAPTURES_POLL_MS } from '@/lib/polling'

function CaptureDetailView({
  data,
}: {
  data?: { summary?: unknown; capture_payload?: unknown }
}) {
  if (!data) return null
  const { summary, capture_payload } = data
  return (
    <div className="space-y-2 border-t p-3 text-sm">
      {summary != null && (
        <p className="whitespace-pre-wrap">
          {typeof summary === 'string' ? summary : JSON.stringify(summary)}
        </p>
      )}
      <details>
        <summary className="text-muted-foreground cursor-pointer">Raw AX-tree</summary>
        <pre className="bg-muted mt-2 overflow-auto rounded p-2 text-xs">
          {JSON.stringify(capture_payload, null, 2)}
        </pre>
      </details>
    </div>
  )
}

function WindowCaptureDetail({ captureId }: { captureId: string }) {
  const capture = useQuery(adminWindowCaptureOptions({ path: { capture_id: captureId } }))
  return <CaptureDetailView data={capture.data} />
}

export function WindowCaptureList({ deviceId }: { deviceId: string }) {
  const queryClient = useQueryClient()
  const options = adminDeviceWindowCapturesInfiniteOptions({ path: { device_id: deviceId } })
  const captures = useInfiniteQuery({
    ...options,
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    refetchInterval: ADMIN_CAPTURES_POLL_MS,
  })
  const clear = useMutation({
    ...adminClearDeviceWindowCapturesMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: options.queryKey }),
  })
  const [openId, setOpenId] = useState<string | null>(null)
  const hasItems = (captures.data?.pages ?? []).some((page) => page.items.length > 0)
  const items = captures.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div className="space-y-2">
      {hasItems && (
        <div className="flex justify-end">
          <ClearCaptureAction
            trigger="Clear raw payloads"
            title="Clear raw AX-tree payloads?"
            description="This removes the stored raw AX-tree payload from every window capture for this device. Capture metadata and summaries are kept."
            confirmLabel="Clear"
            pending={clear.isPending}
            onConfirm={() => clear.mutate({ path: { device_id: deviceId } })}
          />
        </div>
      )}
      {captures.isSuccess && items.length === 0 && (
        <p className="text-muted-foreground">No window captures yet.</p>
      )}
      <ul className="space-y-2">
        {items.map((capture) => (
          <li key={capture.id} className="rounded-lg border">
            <button
              type="button"
              onClick={() => setOpenId(openId === capture.id ? null : capture.id)}
              className="flex w-full items-center justify-between gap-2 p-3 text-left"
            >
              <span className="font-medium">{capture.app ?? 'Unknown app'}</span>
              <Badge variant="secondary">{capture.summary_status}</Badge>
            </button>
            {openId === capture.id && <WindowCaptureDetail captureId={capture.id} />}
          </li>
        ))}
      </ul>
      {captures.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void captures.fetchNextPage()}
          disabled={captures.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </div>
  )
}
